import React, {
    useRef,
    createContext,
    useContext,
    useCallback,
    useSyncExternalStore,
} from "react";

export default function createFastContext<Store>(initialState: Store) {
    function useStoreData(): {
        get: () => Store;
        set: (value: Partial<Store>) => void;
        setAll: (value: Store) => void;
        subscribe: (callback: () => void) => () => void;
    } {
        const store = useRef(initialState);

        const get = useCallback(() => store.current, []);

        const subscribers = useRef(new Set<() => void>());

        const set = useCallback((value: Partial<Store>) => {
            store.current = { ...store.current, ...value };
            subscribers.current.forEach((callback) => callback());
        }, []);

        const setAll = useCallback((value: Store) => {
            store.current = { ...store.current, ...value };
            subscribers.current.forEach((callback) => callback());
        }, []);

        const subscribe = useCallback((callback: () => void) => {
            subscribers.current.add(callback);
            return () => subscribers.current.delete(callback);
        }, []);

        return {
            get,
            set,
            setAll,
            subscribe,
        };
    }

    type UseStoreDataReturnType = ReturnType<typeof useStoreData>;

    const StoreContext = createContext<UseStoreDataReturnType | null>(null);

    const Provider = ({ children }: { children: React.ReactNode }) => {
        return (
            <StoreContext.Provider value={useStoreData()}>
                {children}
            </StoreContext.Provider>
        );
    }


    function useStore<SelectorOutput>(
        selector: (store: Store) => SelectorOutput
    ): [SelectorOutput, (value: Partial<Store>) => void, (value: Store) => void] {
        const store = useContext(StoreContext);
        if (!store) {
            throw new Error("Store not found");
        }

        const state = useSyncExternalStore(
            store.subscribe,
            () => selector(store.get()),
            () => selector(initialState),
        );

        return [state, store.set, store.setAll];
    }

    return {
        Provider,
        useStore,
    };
}